"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";

import BookAppointmentForm from "@/components/utilities/BookAppointmentForm";
import FaqPageSchema from "@/components/schema/FaqPageSchema";
import FAQAccordion from "@/components/utilities/FAQAccordion";
import GoogleRatingAndProcedures from "@/components/utilities/GoogleRatingAndProcedures";
import QuickContactButtons from "@/components/utilities/QuickContactButtons";
import QuickContactForm from "@/components/utilities/QuickContactForm";
import SurgeonCredentials from "@/components/utilities/SurgeonCredentials";
import Slideshow from "@/components/blog/Slideshow";
import NewHeroSection from "@/components/homepage/NewHeroSection";
import ProductShowcaseSection from "@/components/homepage/ProductShowcaseSection";
import TrustStrip from "@/components/homepage/TrustStrip";
import TreatmentsOverview from "@/components/homepage/TreatmentsOverview";
import TreatmentPhilosophy from "@/components/homepage/TreatmentPhilosophy";
import WhoWeHelp from "@/components/homepage/WhoWeHelp";
import ProcessSection from "@/components/homepage/ProcessSection";
import ProcessStepsSection from "@/components/campaign/ProcessSection";
import EducationSection from "@/components/homepage/EducationSection";
import DoctorProfileSection from "@/components/homepage/DoctorProfileSection";
import EbookDownloadSection from "@/components/homepage/EbookDownloadSection";
import SoftCTASection from "@/components/homepage/SoftCTASection";
import AnimatedHeroSectionSanity from "@/components/homepage/AnimatedHeroSectionSanity";
import ThreeColumnCardSection from "@/components/homepage/ThreeColumnCardSection";
import WBannerSection from "@/components/homepage/WBannerSection";
import WTwoColumnSection from "@/components/homepage/WTwoColumnSection";
import WPhotoStripeSection from "@/components/homepage/WPhotoStripeSection";
import WVideoStripeSection from "@/components/homepage/WVideoStripeSection";
import WServicesSection from "@/components/homepage/WServicesSection";
import WProcessStep2Section from "@/components/homepage/WProcessStep2Section";
import WUniqueSellingPointsStrip from "@/components/homepage/WUniqueSellingPointsStrip";
import WOurDoctorsSection from "@/components/homepage/WOurDoctorsSection";
import WReviewsStripSection from "@/components/homepage/WReviewsStripSection";
import WGoogleMapAddressSection from "@/components/homepage/WGoogleMapAddressSection";
import WContactUsSection from "@/components/homepage/WContactUsSection";
import { shouldDisplayWidget } from "./widgetSchedule";

// Map of internal component names to actual components
const INTERNAL_COMPONENTS: Record<
  string,
  React.ComponentType<any> | undefined
> = {
  FAQAccordion,
  QuickContactForm,
  QuickContactButtons,
  BookAppointmentForm,
  GoogleRatingAndProcedures,
  SurgeonCredentials,
  Slideshow,
  // Add more internal components here as needed
};

interface WidgetSchedule {
  displayMode?: "always" | "scheduled";
  weekdaySchedule?: {
    startTime?: string;
    endTime?: string;
  };
  sundaySchedule?: "off" | "custom";
  sundayTimeRange?: {
    startTime?: string;
    endTime?: string;
  };
  overrideDisplay?: {
    enabled?: boolean;
    afterHoursMessage?: string;
  };
}

interface WidgetRendererProps {
  widgetType: "elfsight" | "internal" | "section";
  elfsightWidgetId?: string;
  elfsightScriptUrl?: string;
  internalComponentName?: string;
  internalComponentProps?: string; // JSON string from Sanity
  sectionType?:
    | "hero"
    | "productShowcase"
    | "trustStrip"
    | "treatmentsOverview"
    | "treatmentPhilosophy"
    | "whoWeHelp"
    | "process"
    | "processSteps"
    | "education"
    | "doctorProfile"
    | "faq"
    | "ebookDownload"
    | "finalCta"
    | "animatedHero"
    | "threeColumnCard"
    | "wBanner"
    | "wTwoColumn"
    | "wPhotoStripe"
    | "wVideoStripe"
    | "wServices"
    | "wProcessStep2"
    | "wUniqueSellingPoints"
    | "wOurDoctors"
    | "wReviewsStrip"
    | "wGoogleMapAddress"
    | "wContactUs";
  sectionData?: any; // Section data from Sanity
  maxWidth?: "default" | "wide" | "full";
  backgroundColor?: string;
  isLazy?: boolean;
  schedule?: WidgetSchedule;
}

const MAX_WIDTH_CLASSES: Record<
  NonNullable<WidgetRendererProps["maxWidth"]>,
  string
> = {
  default: "max-w-4xl",
  wide: "max-w-6xl",
  full: "max-w-none",
};

const WidgetRenderer = ({
  widgetType,
  elfsightWidgetId,
  elfsightScriptUrl,
  internalComponentName,
  internalComponentProps,
  sectionType,
  sectionData,
  maxWidth = "default",
  backgroundColor,
  isLazy = true,
  schedule,
}: WidgetRendererProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(!isLazy);
  const [error, setError] = useState<string | null>(null);

  // If overrideDisplay is enabled, always display (form handles message internally)
  const hasOverrideDisplay = schedule?.overrideDisplay?.enabled;
  const [shouldDisplay, setShouldDisplay] = useState(() =>
    hasOverrideDisplay ? true : shouldDisplayWidget(schedule)
  );

  // Check schedule periodically (every minute) to update display
  // Skip if overrideDisplay is enabled (widget always shows)
  useEffect(() => {
    if (hasOverrideDisplay) {
      setShouldDisplay(true);
      return;
    }

    if (!schedule || schedule.displayMode !== "scheduled") return;

    const checkSchedule = () => {
      setShouldDisplay(shouldDisplayWidget(schedule));
    };

    // Check immediately
    checkSchedule();

    // Check every minute
    const interval = setInterval(checkSchedule, 60000);

    return () => clearInterval(interval);
  }, [schedule, hasOverrideDisplay]);

  // Don't render if schedule says not to display (unless overrideDisplay is enabled)
  if (!shouldDisplay) {
    return null;
  }

  // Lazy loading with Intersection Observer
  useEffect(() => {
    if (!isLazy || isVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: "50px" }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isLazy, isVisible]);

  // Load Elfsight script
  useEffect(() => {
    if (widgetType !== "elfsight" || !isVisible) return;

    const scriptId = "elfsight-platform-script";
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src =
      elfsightScriptUrl || "https://static.elfsight.com/platform/platform.js";
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => setError("Failed to load Elfsight platform script");

    document.head.appendChild(script);

    return () => {
      // Don't remove script on unmount as it might be used by other widgets
    };
  }, [widgetType, isVisible, elfsightScriptUrl]);

  // Parse internal component props
  let parsedProps: Record<string, any> = {};
  if (internalComponentProps) {
    try {
      parsedProps = JSON.parse(internalComponentProps);
    } catch (e) {
      console.error("Failed to parse internal component props:", e);
      setError("Invalid component props format");
    }
  }

  const widthClass = MAX_WIDTH_CLASSES[maxWidth] || MAX_WIDTH_CLASSES.default;

  // Render Elfsight widget
  if (widgetType === "elfsight") {
    if (!elfsightWidgetId) {
      return (
        <div className="my-4 rounded-lg border border-red-300 bg-red-50 p-4 text-red-700">
          Error: Elfsight Widget ID is missing
        </div>
      );
    }

    if (error) {
      return (
        <div className="my-4 rounded-lg border border-red-300 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      );
    }

    const widgetClass = `elfsight-app-${elfsightWidgetId}`;

    return (
      <div ref={containerRef} className={`mx-auto w-full ${widthClass}`}>
        {isVisible && (
          <>
            {!scriptLoaded && (
              <Script
                src={
                  elfsightScriptUrl ||
                  "https://static.elfsight.com/platform/platform.js"
                }
                strategy="lazyOnload"
                onLoad={() => setScriptLoaded(true)}
                onError={() => setError("Failed to load Elfsight script")}
              />
            )}
            <div
              className={widgetClass}
              {...(isLazy && { "data-elfsight-app-lazy": true })}
            />
          </>
        )}
        {!isVisible && (
          <div className="flex min-h-[200px] items-center justify-center rounded-lg bg-gray-50 text-gray-400">
            Loading widget...
          </div>
        )}
      </div>
    );
  }

  // Render internal widget
  if (widgetType === "internal") {
    if (!internalComponentName) {
      return (
        <div className="my-4 rounded-lg border border-red-300 bg-red-50 p-4 text-red-700">
          Error: Internal Component Name is missing
        </div>
      );
    }

    const Component = INTERNAL_COMPONENTS[internalComponentName];

    if (!Component) {
      return (
        <div className="my-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-yellow-700">
          Warning: Component &quot;{internalComponentName}&quot; not found.
          Available components: {Object.keys(INTERNAL_COMPONENTS).join(", ")}
        </div>
      );
    }

    // Special handling for BookAppointmentForm - pass overrideDisplay if available
    // Special handling for QuickContactForm - pass formConfig if available
    // Special handling for Slideshow - transform slideshow data to slides format
    let finalProps = parsedProps;
    if (
      internalComponentName === "BookAppointmentForm" &&
      parsedProps.overrideDisplay
    ) {
      finalProps = { overrideDisplay: parsedProps.overrideDisplay };
    } else if (
      internalComponentName === "QuickContactForm" &&
      parsedProps.formConfig
    ) {
      finalProps = parsedProps; // formConfig is already in parsedProps
    } else if (internalComponentName === "Slideshow" && parsedProps.slideshow) {
      // Transform slideshow data to Slideshow component format
      const slideshowData = parsedProps.slideshow;

      const slides =
        slideshowData.images
          ?.map((image: any, index: number) => {
            const secureUrl =
              image?.asset?.secure_url ||
              image?.asset?.url ||
              image?.imageUrl;

            if (!secureUrl) {
              return null;
            }

            const caption = image?.caption || "";
            const alt = image?.alt || "Slideshow image";

            return {
              id: image?._key || `slide-${index}`,
              secureUrl,
              alt,
              caption,
            };
          })
          .filter((slide: any) => slide !== null) || [];

      if (slides.length === 0) {
        return null;
      }

      finalProps = {
        slides,
        autoplay: Boolean(slideshowData.autoplay),
        delayMs: slideshowData.delayMs || 5000,
        maxWidth: parsedProps.maxWidth || "default",
      };
    }

    return (
      <div ref={containerRef} className={`mx-auto w-full ${widthClass}`}>
        {isVisible ? (
          <Component {...finalProps} />
        ) : (
          <div className="flex min-h-[200px] items-center justify-center rounded-lg bg-gray-50 text-gray-400">
            Loading widget...
          </div>
        )}
      </div>
    );
  }

  // Render section widget
  if (widgetType === "section") {
    if (!sectionType) {
      return (
        <div className="my-4 rounded-lg border border-red-300 bg-red-50 p-4 text-red-700">
          Error: Section type is missing
        </div>
      );
    }
    if (!sectionData) {
      if (sectionType === "wGoogleMapAddress") {
        return <WGoogleMapAddressSection />;
      }
      if (sectionType === "wContactUs") {
        return <WContactUsSection />;
      }
      console.warn("Section data is missing for sectionType:", sectionType);
      return (
        <div className="my-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-yellow-700">
          Warning: Section data is missing for &quot;{sectionType}&quot;. Please check that the section reference is properly linked in Sanity.
        </div>
      );
    }

    const widthClass = MAX_WIDTH_CLASSES[maxWidth];

    // Map section type to component
    const renderSection = () => {
      switch (sectionType) {
        case "hero":
          return <NewHeroSection {...sectionData} />;
        case "productShowcase":
          return <ProductShowcaseSection {...sectionData} />;
        case "trustStrip":
          return (
            <TrustStrip
              {...sectionData}
              maxWidth={sectionData?.maxWidth ?? maxWidth}
            />
          );
        case "treatmentsOverview":
          return <TreatmentsOverview {...sectionData} />;
        case "treatmentPhilosophy":
          return <TreatmentPhilosophy {...sectionData} />;
        case "whoWeHelp":
          return <WhoWeHelp {...sectionData} />;
        case "process":
          return <ProcessSection {...sectionData} />;
        case "processSteps":
          return <ProcessStepsSection {...sectionData} />;
        case "education":
          return <EducationSection {...sectionData} />;
        case "doctorProfile":
          return <DoctorProfileSection {...sectionData} />;
        case "faq": {
          const faqs = sectionData.faqs || [];
          const hasFaqs = Array.isArray(faqs) && faqs.length > 0;
          return (
            <section className="mx-auto w-full max-w-6xl py-6 md:py-8" aria-label="FAQs">
              <FaqPageSchema faqs={faqs} />
              <div className="mx-auto w-full px-4 md:px-6">
                {hasFaqs && (
                  <div className="mb-6 grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3">
                    <div
                      className="h-0.5 w-[35%] min-w-[80px] justify-self-end"
                      style={{
                        background: "linear-gradient(to right, transparent, #2e1065)",
                      }}
                      aria-hidden
                    />
                    <h2 className="text-center font-extrabold uppercase tracking-wide text-violet-950 md:text-xl lg:text-2xl">
                      FAQs
                    </h2>
                    <div
                      className="h-0.5 w-[35%] min-w-[80px] justify-self-start"
                      style={{
                        background: "linear-gradient(to right, #2e1065, transparent)",
                      }}
                      aria-hidden
                    />
                  </div>
                )}
                <FAQAccordion
                  faqs={faqs}
                  title={sectionData.heading}
                  variant="homepage"
                />
              </div>
            </section>
          );
        }
        case "ebookDownload":
          return <EbookDownloadSection {...sectionData} />;
        case "animatedHero":
          return <AnimatedHeroSectionSanity {...sectionData} />;
        case "finalCta":
          return <SoftCTASection {...sectionData} />;
        case "threeColumnCard":
          return <ThreeColumnCardSection {...sectionData} />;
        case "wBanner":
          return <WBannerSection {...sectionData} />;
        case "wTwoColumn":
          return <WTwoColumnSection {...sectionData} />;
        case "wPhotoStripe":
          return <WPhotoStripeSection {...sectionData} />;
        case "wVideoStripe":
          return <WVideoStripeSection {...sectionData} />;
        case "wServices":
          return <WServicesSection {...sectionData} />;
        case "wProcessStep2":
          return <WProcessStep2Section {...sectionData} />;
        case "wUniqueSellingPoints":
          return <WUniqueSellingPointsStrip {...sectionData} />;
        case "wOurDoctors":
          return <WOurDoctorsSection {...sectionData} />;
        case "wReviewsStrip":
          return <WReviewsStripSection {...sectionData} />;
        case "wGoogleMapAddress":
          return <WGoogleMapAddressSection {...sectionData} />;
        case "wContactUs":
          return <WContactUsSection {...sectionData} />;
        default:
          return (
            <div className="my-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-yellow-700">
              Unknown section type: {sectionType}
            </div>
          );
      }
    };

    // For threeColumnCard section, use full width container
    const containerClass = sectionType === "threeColumnCard" 
      ? "mx-auto w-full" 
      : `mx-auto w-full ${widthClass}`;

    const containerStyle =
      backgroundColor && backgroundColor.trim() !== ""
        ? { backgroundColor: backgroundColor.trim() }
        : undefined;

    return (
      <div
        ref={containerRef}
        className={containerClass}
        style={containerStyle}
      >
        {isVisible ? (
          renderSection()
        ) : (
          <div className="flex min-h-[200px] items-center justify-center rounded-lg bg-gray-50 text-gray-400">
            Loading widget...
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default WidgetRenderer;
